// Firefox throttles `history.pushState`/`replaceState` to 100 combined
// calls per 10 seconds and raises a `SecurityError` past that. The search
// page has three independent systems writing the URL (InstantSearch's
// router, the map's `setMapBounds`, and wouter custom-param updates) which
// can interleave or loop during on-load reconciliation and blow that
// budget. These helpers keep the write count down by skipping no-op writes
// — i.e. when the URL we're about to write already matches the current one.
//
// Crucially the three writers do NOT serialise the query string the same
// way. InstantSearch's router builds its URL with
// `qsModule.stringify(routeState, { encode: false })`, so it emits literal
// spaces (`location=New York`) and orders refinement params before the
// preserved `bounds_*`/`country`. The map / wouter writers go through
// `URLSearchParams.toString()`, which `+`-encodes spaces (`location=New+York`)
// and keeps the params in their existing order. A naive string compare
// therefore treats two byte-different-but-semantically-identical URLs as a
// real change, so each writer "corrects" the other's encoding/ordering on
// the next render — a tit-for-tat `replaceState` fight that hammers the
// throttle. To stop that, the change-guard compares URLs *semantically*:
// decode every param value and sort the keys into a canonical form before
// comparing, so encoding- and ordering-only differences are true no-ops.

/**
 * Canonicalises a (possibly relative) target URL to a comparable
 * `pathname + ?sortedQuery` form. Query params are decoded and re-encoded
 * via `URLSearchParams` (so `New York` and `New+York` collapse to the same
 * value) and sorted by key (so refinement-first vs bounds-first ordering
 * collapse too). The result is only ever used for *comparison* — the actual
 * `replaceState`/`setLocation` call still receives the caller's original
 * string, so the visible URL is unchanged.
 */
export function normalizeTarget(url: string): string {
  if (typeof window === "undefined") return url;
  try {
    const u = new URL(url, window.location.origin);
    // `.sort()` orders by key and keeps the relative order of repeated
    // keys (e.g. multiple `car_type` values), which both writers emit in
    // the same insertion order — so the multiset is preserved. `.toString()`
    // then re-encodes every value consistently.
    u.searchParams.sort();
    const qs = u.searchParams.toString();
    return u.pathname + (qs ? `?${qs}` : "");
  } catch {
    return url;
  }
}

function currentNormalized(): string {
  if (typeof window === "undefined") return "";
  return normalizeTarget(window.location.pathname + window.location.search);
}

/**
 * True when navigating to `url` would not change the path or query, compared
 * semantically (param encoding and ordering are ignored — see module note).
 */
export function isSameLocation(url: string): boolean {
  return normalizeTarget(url) === currentNormalized();
}

// Firefox/WebKit throttle `replaceState`/`pushState` to ~100 calls per 10s and
// raise a `SecurityError` once that ceiling is crossed (Sentry
// DRIVESHARE_V2-95). De-duping no-op writes (above) removes most churn, but a
// continuous map drag/pinch-zoom fires a stream of genuinely-DISTINCT bounds
// that can still exceed the budget. So we additionally:
//   (a) cap real writes to a margin below the browser ceiling per rolling 10s
//       window, coalescing any overflow into a single TRAILING write of the
//       latest URL (so the final viewport always lands once budget frees up);
//   (b) wrap the native call in try/catch so a throttle `SecurityError` — or
//       any other history failure — can never surface as an unhandled error.
import { recentLoopGuardWriteCount } from "@/lib/navigation-loop-guard";

const WRITE_WINDOW_MS = 10_000;
// Stay below the browser's 100/10s ceiling — wouter and the InstantSearch
// router also write history through their own paths, so leave headroom.
const MAX_WRITES_PER_WINDOW = 80;
let writeTimestamps: number[] = [];
let pendingTrailingUrl: string | null = null;
let trailingTimer: ReturnType<typeof setTimeout> | null = null;

function pruneWriteWindow(now: number): void {
  const cutoff = now - WRITE_WINDOW_MS;
  if (writeTimestamps.length > 0 && writeTimestamps[0] < cutoff) {
    writeTimestamps = writeTimestamps.filter((t) => t >= cutoff);
  }
}

function rawReplaceState(url: string): boolean {
  try {
    window.history.replaceState(null, "", url);
    writeTimestamps.push(Date.now());
    return true;
  } catch {
    // Throttle SecurityError (or any history failure): swallow it so it never
    // pages. The trailing scheduler re-attempts the latest URL shortly.
    return false;
  }
}

function flushTrailingWrite(): void {
  trailingTimer = null;
  if (pendingTrailingUrl == null) return;
  const url = pendingTrailingUrl;
  pendingTrailingUrl = null;
  // Re-enter the budgeted path; if still saturated it reschedules itself.
  replaceStateIfChanged(url);
}

function scheduleTrailingWrite(url: string): void {
  pendingTrailingUrl = url;
  if (trailingTimer != null) return;
  const now = Date.now();
  pruneWriteWindow(now);
  // Resume writing exactly when the oldest write in the window ages out (plus
  // a small cushion), so we never busy-poll.
  const oldest = writeTimestamps[0] ?? now;
  const wait = Math.max(0, oldest + WRITE_WINDOW_MS - now) + 50;
  trailingTimer = setTimeout(flushTrailingWrite, wait);
}

/**
 * `history.replaceState` that no-ops when the target URL already matches the
 * current location, and that rate-limits real writes to stay under the
 * browser's throttle (overflow is coalesced into a single trailing write of
 * the latest URL). Used by the map-bounds writer and the InstantSearch router
 * override so transient churn doesn't pile up redundant mutations or trip the
 * `SecurityError` throttle.
 */
export function replaceStateIfChanged(url: string): boolean {
  if (typeof window === "undefined") return false;
  if (isSameLocation(url)) {
    // The current URL already matches; any queued trailing write that targets
    // this same location is now stale.
    if (pendingTrailingUrl != null && isSameLocation(pendingTrailingUrl)) {
      pendingTrailingUrl = null;
    }
    return false;
  }
  const now = Date.now();
  pruneWriteWindow(now);
  // Combined budget: our own writes PLUS loop-guard-approved rewrites (locale
  // canonicalisation etc.) share the same browser throttle, so count both.
  if (writeTimestamps.length + recentLoopGuardWriteCount(now) >= MAX_WRITES_PER_WINDOW) {
    scheduleTrailingWrite(url);
    return false;
  }
  return rawReplaceState(url);
}

/** Test helper — clears the rolling write budget and any queued trailing write. */
export function resetHistoryWriteBudget(): void {
  writeTimestamps = [];
  pendingTrailingUrl = null;
  if (trailingTimer != null) {
    clearTimeout(trailingTimer);
    trailingTimer = null;
  }
}
